import { lazy, Suspense } from "react";
import Hero from "@/components/Hero";
import ValueProps from "@/components/ValueProps";
import ScrollReveal from "@/components/ScrollReveal";
import StickyCalculatorHeader from "@/components/StickyCalculatorHeader";
import SEO from "@/components/SEO";
import { useTranslation } from "react-i18next";

// Lazy load below-the-fold components for code splitting
const ComparisonSlider = lazy(() => import("@/components/ComparisonSlider"));
const BeforeAfterSlider = lazy(() => import("@/components/BeforeAfterSlider"));
const Storytelling = lazy(() => import("@/components/Storytelling"));
const ProofSection = lazy(() => import("@/components/ProofSection"));
const Features = lazy(() => import("@/components/Features"));
const SocialProof = lazy(() => import("@/components/SocialProof"));
const TrustedBy = lazy(() => import("@/components/TrustedBy"));
const VideoTestimonials = lazy(() => import("@/components/VideoTestimonials"));
const FAQ = lazy(() => import("@/components/FAQ"));
const FinalCTA = lazy(() => import("@/components/FinalCTA"));
const FloatingBookingButton = lazy(() => import("@/components/FloatingBookingButton"));
const CorporateCostSection = lazy(() => import("@/components/CorporateCostSection"));
const Footer = lazy(() => import("@/components/Footer"));

const Index = () => {
  const { t } = useTranslation();
  const hotelComparison = {
    left: {
      title: t('comparison.hotel.leftTitle'),
      items: t('comparison.hotel.leftItems', { returnObjects: true }) as string[],
    },
    right: {
      title: t('comparison.hotel.rightTitle'),
      items: t('comparison.hotel.rightItems', { returnObjects: true }) as string[],
    },
  };

  const airbnbComparison = {
    left: {
      title: t('comparison.airbnb.leftTitle'),
      items: t('comparison.airbnb.leftItems', { returnObjects: true }) as string[],
    },
    right: {
      title: t('comparison.airbnb.rightTitle'),
      items: t('comparison.airbnb.rightItems', { returnObjects: true }) as string[],
    },
  };

  const diyComparison = {
    left: {
      title: t('comparison.diy.leftTitle'),
      items: t('comparison.diy.leftItems', { returnObjects: true }) as string[],
    },
    right: {
      title: t('comparison.diy.rightTitle'),
      items: t('comparison.diy.rightItems', { returnObjects: true }) as string[],
    },
  };

  return (
    <>
      <SEO page="home" />
      <main className="min-h-screen">
        <StickyCalculatorHeader />
      <Hero />
      
      <ScrollReveal>
        <ValueProps />
      </ScrollReveal>
      
      <Suspense fallback={<div className="min-h-screen" />}>
        <ScrollReveal delay={0.1}>
          <ComparisonSlider
            headline={t('comparison.hotel.headline')}
            left={hotelComparison.left}
            right={hotelComparison.right}
          />
        </ScrollReveal>
        
        <ScrollReveal delay={0.1}>
          <ComparisonSlider
            headline={t('comparison.airbnb.headline')}
            left={airbnbComparison.left}
            right={airbnbComparison.right}
            reverse
          />
        </ScrollReveal>
        
        <ScrollReveal delay={0.1}>
          <ComparisonSlider
            headline={t('comparison.diy.headline')}
            left={diyComparison.left}
            right={diyComparison.right}
          />
        </ScrollReveal>
        
        <ScrollReveal>
          <BeforeAfterSlider />
        </ScrollReveal>
        
        <Storytelling />
        
        <ScrollReveal>
          <ProofSection />
        </ScrollReveal>
        
        <ScrollReveal>
          <Features />
        </ScrollReveal>

        {/* Corporate Cost Calculator Section */}
        <ScrollReveal>
          <CorporateCostSection />
        </ScrollReveal>
        
        <ScrollReveal>
          <SocialProof />
        </ScrollReveal>
        
        <ScrollReveal>
          <VideoTestimonials />
        </ScrollReveal>
        
        <ScrollReveal>
          <TrustedBy />
        </ScrollReveal>
        
        <ScrollReveal>
          <FinalCTA />
        </ScrollReveal>
        
        <ScrollReveal>
          <FAQ />
        </ScrollReveal>

        <Footer />

        <FloatingBookingButton />
      </Suspense>
      </main>
    </>
  );
};

export default Index;
